import Quill, { Delta } from 'quill/core';
import React, { useCallback, useState } from 'react';
import { useAppDispatch } from '../../../app/redux/useAppSelector';
import { UIActions } from '../../../ui/uiSlice';
import addTestId from '../../../utils/addTestId';
import Link from '../../../utils/router/Link';
import safeParseUrl from '../../../utils/url/safeParseUrl';
import {
  useQuillMutations,
  useQuillSelection,
  useQuillState,
} from '../../QuillContext';
import type QuillEditor from '../../QuillEditor';
import { getRangeSaver } from '../../RangeSaver';
import matchCurrentUrl from '../../utils/matchCurrentUrl';
import { copyIcon, editLinkIcon, unlinkIcon } from '../editorIcons';
import ToolbarButton, { ToolbarButtonGroup } from './buttons/ToolbarButton';
import LinkEditor from './LinkEditor';
import * as styles from './LinkToolbar.module.scss';

interface LinkToolbarProps {
  quill: QuillEditor;
  onLinkChange: (value: string | null) => void;
}

const LinkToolbar = ({ onLinkChange, quill }: LinkToolbarProps) => {
  const [editingTarget, setEditingTarget] = useState<'link' | 'text'>('link');

  const dispatch = useAppDispatch();
  const { isEditingLink } = useQuillState();
  const { editingLinkChange } = useQuillMutations();
  const { formats, range: currentRange } = useQuillSelection();

  const { link } = formats;
  const text = currentRange ? quill.getText(currentRange) : '';
  const [currentLeaf] = currentRange
    ? quill.getLeaf(currentRange.index + currentRange.length)
    : [null];

  const expandLinkSelection = useCallback(() => {
    const range = getRangeSaver(quill).getLast();
    const [leaf, offset] = quill.getLeaf(range.index + range.length);
    if (leaf) {
      quill.setSelection(range.index - (offset - range.length), leaf.length());
    }
  }, [quill]);

  const handleSave = (value: string | null) => {
    const range = getRangeSaver(quill).getLast();

    quill.focusWithoutScrolling();
    if (range.length) {
      quill.setSelection(range);
    } else {
      // Expand the range to cover the entire link if the selection is collapsed
      expandLinkSelection();
    }

    if (editingTarget === 'link') {
      onLinkChange(value);
    } else {
      const selection = quill.getSelection();

      if (selection && value) {
        quill.history.cutoff();

        quill.updateContents(
          new Delta()
            .retain(selection.index)
            .delete(selection.length)
            .insert(value, formats),
          Quill.sources.USER,
        );

        quill.history.cutoff();
      }
    }

    editingLinkChange(false);
  };

  const firstLink =
    (Array.isArray(link) ? (link[0] as string) : (link as string)) ?? '';
  const parsedLink = safeParseUrl(firstLink, window.location.href);
  const { hasCurrentPostId } = matchCurrentUrl({
    target: firstLink,
    // we don't need to consider embeds for this one,
    // because LinkToolbar won't be rendered inside an embed
    currentUrl: window.location.href,
  });

  const shouldScroll = !!parsedLink?.hash && hasCurrentPostId;

  return (
    <div className={styles.container}>
      {isEditingLink && currentLeaf ? (
        <LinkEditor
          blot={currentLeaf}
          initialLink={firstLink}
          initialText={text}
          editingTarget={editingTarget}
          linkPlaceholder="Paste a link"
          textPlaceholder="Paste a text"
          onEditingTargetChange={(target) => {
            if (target) {
              setEditingTarget(target);
            } else {
              editingLinkChange(false);
            }
          }}
          onSave={handleSave}
        />
      ) : (
        <>
          <Link
            className={styles.link}
            to={shouldScroll ? parsedLink.hash : firstLink}
            newTab={shouldScroll ? false : 'ugc'}
            {...addTestId('linkToolbarLinkField')}
          >
            {firstLink}
          </Link>
          <ToolbarButtonGroup>
            <ToolbarButton
              tooltip="Copy"
              icon={copyIcon}
              onClick={async () => {
                await navigator.clipboard.writeText(firstLink);
                dispatch(
                  UIActions.pushToast({
                    level: 'success',
                    message: 'Link copied',
                  }),
                );
                quill.focus();
              }}
            />
            <ToolbarButton
              icon={editLinkIcon}
              tooltip="Edit"
              active={isEditingLink}
              onClick={() => {
                expandLinkSelection();
                editingLinkChange(true);
              }}
            />
            <ToolbarButton
              icon={unlinkIcon}
              tooltip="Remove"
              onClick={() => handleSave(null)}
            />
          </ToolbarButtonGroup>
        </>
      )}
    </div>
  );
};

export default LinkToolbar;
